import React from "react";
import {useTranslation} from "react-i18next";
import {THEME} from "@/enums/profile";
import {cn} from "@/lib/utils";
import type {ChatChartData, ChatFileData} from "@/types/chatMessages";
import AskChatBubble from "./AskChatBubble";
import AskFileBubble from "./AskFileBubble";
import AskGeneratedFileBubble from "./AskGeneratedFileBubble";
import ChartContent from "./ChartContent";

type BaseMessageProps = {
   isUser?: boolean;
   agentName?: string;
   themeOverride?: THEME;
   brandOverride?: string;
   className?: string;
   isLastInGroup?: boolean;
   /** Skip the Gail/agent header — used when this bubble follows another assistant bubble in the same turn. */
   hideAgentHeader?: boolean;
};

type RegularMessageProps = BaseMessageProps & {
   type: "regular";
   children?: React.ReactNode;
   isWelcomeMessage?: boolean;
   copyText?: string;
   readAloudSlot?: React.ReactNode;
   feedbackSlot?: React.ReactNode;
};

type FileMessageProps = BaseMessageProps & {
   type: "file";
   fileData: ChatFileData;
   fileName: string;
   autoOpen?: boolean;
   /** Copy/feedback row to render below the file card when this is the last message in an assistant turn. */
   feedbackSlot?: React.ReactNode;
   copyText?: string;
};

type ChartMessageProps = BaseMessageProps & {
   type: "chart";
   chartData: ChatChartData;
};

type MessageBubbleProps = RegularMessageProps | FileMessageProps | ChartMessageProps;

const MessageBubble: React.FC<MessageBubbleProps> = (props) => {
   const {t} = useTranslation("ask");

   if (props.type === "file") {
      if (props.fileData.type === "generated") {
         return (
            <AskGeneratedFileBubble
               isUser={props.isUser}
               agentName={props.agentName}
               brandOverride={props.brandOverride}
               className={props.className}
               downloadUrl={props.fileData.downloadUrl}
               fileName={props.fileName}
               mimeType={props.fileData.mimeType}
               objectPath={props.fileData.objectPath}
               isLastInGroup={props.isLastInGroup}
               autoOpen={props.autoOpen}
               hideAgentHeader={props.hideAgentHeader}
               feedbackSlot={props.feedbackSlot}
               copyText={props.copyText}
            />
         );
      }
      return (
         <AskFileBubble
            isUser={props.isUser}
            agentName={props.agentName}
            brandOverride={props.brandOverride}
            className={props.className}
            fileData={props.fileData}
            fileName={props.fileName}
            isLastInGroup={props.isLastInGroup}
            autoOpen={props.autoOpen}
            hideAgentHeader={props.hideAgentHeader}
            feedbackSlot={props.feedbackSlot}
            copyText={props.copyText}
         />
      );
   }

   if (props.type === "chart") {
      if (!props.chartData?.data) {
         return (
            <AskChatBubble
               isUser={props.isUser}
               agentName={props.agentName}
               themeOverride={props.themeOverride}
               brandOverride={props.brandOverride}
               className={props.className}
               isNewGailGpt
            >
               <div>{t("chartFailed")}</div>
            </AskChatBubble>
         );
      }

      return (
         <AskChatBubble
            isUser={props.isUser}
            agentName={props.agentName}
            themeOverride={props.themeOverride}
            brandOverride={props.brandOverride}
            className={cn(props.className, "min-w-[60%] object-contain")}
            isLastInGroup={props.isLastInGroup}
            isNewGailGpt
         >
            <div className="bg-background rounded-2xl border border-neutral py-4">
               <ChartContent
                  chartData={props.chartData}
                  themeOverride={props.themeOverride}
                  brandOverride={props.brandOverride}
               />
            </div>
         </AskChatBubble>
      );
   }

   return (
      <AskChatBubble
         isUser={props.isUser}
         isWelcomeMessage={props.isWelcomeMessage}
         agentName={props.agentName}
         themeOverride={props.themeOverride}
         brandOverride={props.brandOverride}
         className={props.className}
         isLastInGroup={props.isLastInGroup}
         isNewGailGpt
         showCopy={!props.isUser && Boolean(props.copyText?.trim())}
         copyText={props.copyText}
         readAloudSlot={props.readAloudSlot}
         feedbackSlot={props.feedbackSlot}
      >
         {props.children}
      </AskChatBubble>
   );
};

export default MessageBubble;
